import useAuthStore, { MapEngineMode } from '@/store/auth';
import { useRouter } from 'next/router';
import React, { ReactElement, ReactNode, useEffect } from 'react';

import apiConfig from '@/config/url-api';
import { useQueryUser } from '@/api/queries/auth';
import { ca } from 'date-fns/locale';
import useVesselsStore from '@/store/vessels';
import { isWebGLSupported } from '@/utils/hardwareDetection';

interface Props {
  children: ReactNode;
  fallback: ReactElement;
}

export default function AuthGuard(props: Props) {
  const { children, fallback } = props;

  const router = useRouter();
  const authStore = useAuthStore((state) => state);

  const vesselStore = useVesselsStore((state) => state);
  const { vessels } = vesselStore;

  const { isError, data, refetch } = useQueryUser();

  useEffect(() => {
    if (!router.isReady) return;

    // Embed routes handle their own auth state and error screens
    if (router.pathname?.startsWith('/apps-embed')) {
      return;
    }

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code') || (router.query as any)?.code;
      if (code) {
        window.localStorage.setItem(apiConfig.auth.storageTokenKeyName, String(code));
        url.searchParams.delete('code');
        window.history.replaceState(window.history.state, '', url.pathname + (url.search ? url.search : '') + url.hash);
      }
    }

    const storedToken = typeof window !== 'undefined' ? window.localStorage.getItem(apiConfig.auth.storageTokenKeyName) : null;

    if (authStore.user === null && !storedToken) {
      if (router.asPath !== '/') {
        window.location.href = `${window.location.origin}/login?returnUrl=${router.asPath}`;
      } else {
        window.location.href = `${window.location.origin}/login`;
      }
    } else if (authStore.user === null) {
      refetch();
    }
  }, [router.isReady, router.route]);

  useEffect(() => {
    if (data) {
      /**
       * determine dark or light theme based on user preference
       */

      if (data?.settings) {
        try {
          const userSettings = JSON.parse(data?.settings);
          if (userSettings.theme === 'dark') {
            document.documentElement.classList.add('dark');
          }

          if (userSettings.map_mode && typeof window !== 'undefined') {
            let normalized: MapEngineMode = userSettings.map_mode === 'leaflet' || userSettings.map_mode === 'maps-leaflet' ? 'maps-leaflet' : 'maps-gl';
            // Safety Guard: jika device/browser tidak mendukung WebGL, paksa fallback ke leaflet
            if (normalized === 'maps-gl' && !isWebGLSupported()) {
              normalized = 'maps-leaflet';
            }
            window.localStorage.setItem('map_mode', normalized);
            authStore.setMapMode(normalized, false);
          }

          data.settingsObj = userSettings;
        } catch (e) {
          console.log('Invalid options');
        }
      }

      authStore.setLogin(data);
    } else if (isError) {
      if (router.pathname?.startsWith('/apps-embed')) {
        return;
      }
      window.localStorage.removeItem(apiConfig.auth.storageTokenKeyName);
      window.localStorage.removeItem(apiConfig.auth.storageUserKeyName);
      window.location.href = `${window.location.origin}/login?returnUrl=${router.asPath}`;
    }
  }, [data, isError]);

  if (authStore.user === null) {
    if (router.pathname?.startsWith('/apps-embed')) {
      return <>{children}</>;
    }
    return fallback;
  }

  return <>{children}</>;
}
